/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React, { useState, useEffect } from "react";
import axios from "../api/axiosInstance"; // ← con token automático
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import {
    buildRatesPricesUrl,
    buildReservationStudioUrl,
    buildReservationUpdatePayload,
    getReservationWorkflowBusinessUnitError,
} from "./editReservationPayload";

const EditReservation = () => {
    const { baseCurrency } = useCurrencyDisplay();
    const { id } = useParams(); // Obtener ID de la reserva desde la URL
    const [searchParams] = useSearchParams();
    const businessUnitId = searchParams.get("business_unit_id");
    let roomIdFromURL = searchParams.get("room_id");
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        check_in_date: "",
        check_in_time: "",
        check_out_date: "",
        check_out_time: "",
        room_category_id: "",
        room_id: "",
        rate_id: "",
        room_nights: 1,
        adults: 1,
        children: 0,
        children_ages: "",
        babies: 0,
        special_requests: "",
        payment_status: "Pending",
        total_price: "",
        cancellation_reason: "",
        status: "Confirmed",
        business_unit_id: 1,
        reservation_group_id: null,
        
    });
    const [originalFormData, setOriginalFormData] = useState(null);
    
    const [rooms, setRooms] = useState([]);
    const [rateLabel, setRateLabel] = useState("");
    const [roomCategoryLabel, setRoomCategoryLabel] = useState("");


 // Cargar datos de la reserva al montar el componente
 useEffect(() => {
    
    console.log("🔍 room_id en la URL al entrar a editar:", roomIdFromURL);

    axios.get(`${process.env.REACT_APP_API_URL}/api/reservations/${id}`)
        .then(async (response) => {
            console.log("Datos de la reserva:", response.data);
            console.log("Room asignada inicialmente:", response.data.effective_room_id ?? response.data.room_id);
            console.log("Huéspedes:", response.data.guests);

            const roomIdFromURL = new URLSearchParams(window.location.search).get("room_id");
            console.log("🔍 room_id en la URL antes de asignar a formData:", roomIdFromURL); // 📌 Nuevo log

            const effectiveRoomId = response.data.effective_room_id ?? response.data.room_id ?? null;

            let updatedFormData = {
                check_in_date: response.data.check_in_date || "",
                check_in_time: response.data.check_in_time || "",
                check_out_date: response.data.check_out_date || "",
                check_out_time: response.data.check_out_time || "",
                room_category_id: response.data.room_category_id || "",
                room_id: effectiveRoomId || roomIdFromURL || "",
                rate_id: response.data.rate_id || "",
                room_nights: response.data.room_nights || 1,
                adults: response.data.adults || 1,
                children: response.data.children || 0,
                children_ages: response.data.children_ages || "",
                babies: response.data.babies || 0,
                special_requests: response.data.special_requests || "",
                payment_status: response.data.payment_status || "Pending",
                total_price: response.data.total_price || "",
                cancellation_reason: response.data.cancellation_reason || "",
                status: response.data.status || "Confirmed",
                business_unit_id: response.data.business_unit_id || 1,
                reservation_group_id: response.data.reservation_group_id || null,
                guests: response.data.guests || [] // ✅ Recuperamos los huéspedes
            };

            setRateLabel(
                response.data.rate?.name ||
                response.data.group_rate_name ||
                (response.data.rate_id ? `Rate #${response.data.rate_id}` : "No rate assigned")
            );
            setRoomCategoryLabel(
                response.data.room_category?.name ||
                (response.data.room_category_id
                    ? `Category #${response.data.room_category_id}`
                    : "No category assigned")
            );
            

            // 🔹 Si room_id es null, buscamos en reservation_history
            if (!effectiveRoomId) {
                try {
                    const { data: historyData } = await axios.get(`${process.env.REACT_APP_API_URL}/api/reservations/${id}/last-room`);
                    if (historyData?.previous_room_id) {
                        console.log(`🔄 Recuperando último room_id registrado: ${historyData.previous_room_id}`);
                        updatedFormData = { 
                            ...updatedFormData, 
                            room_id: historyData.previous_room_id,  // 🔹 Ahora `room_id` se asigna correctamente
                            previous_room_number: historyData.previous_room_number || `Room ${historyData.previous_room_id}`
                        };
                        
                    }
                } catch (historyError) {
                    console.error("❌ Error obteniendo el último room_id de reservation_history:", historyError);
                }
            }

            setFormData(updatedFormData);
            setOriginalFormData({ ...updatedFormData });
        })
        .catch(error => {
            console.error("Error fetching reservation details:", error);
        });
}, [id, roomIdFromURL]);

useEffect(() => {
    const effectiveBusinessUnitId = formData.business_unit_id || businessUnitId;
    if (formData.check_in_date && formData.check_out_date && effectiveBusinessUnitId && formData.room_category_id) {
        console.log("🔍 Solicitando habitaciones disponibles con:", {
            business_unit_id: effectiveBusinessUnitId,
            room_category_id: formData.room_category_id, 
            check_in_date: formData.check_in_date,
            check_out_date: formData.check_out_date
        });

        axios.get(`${process.env.REACT_APP_API_URL}/api/rooms/available`, {
            params: {
                business_unit_id: effectiveBusinessUnitId,
                room_category_id: formData.room_category_id, 
                check_in_date: formData.check_in_date,
                check_out_date: formData.check_out_date,
                exclude_reservation_id: id,
            }
        })
        .then(response => {
            let availableRooms = response.data;
            console.log("📡 Habitaciones disponibles recibidas:", availableRooms);

            // 🔹 Si la habitación actual no está en la lista, agregarla manualmente
            if (formData.room_id && !availableRooms.some(room => room.id === Number(formData.room_id))) {
                console.log(`❗ La habitación ${formData.room_id} NO está en la lista de habitaciones disponibles.`);
                
                // Buscar la habitación manualmente en la API
                axios.get(`${process.env.REACT_APP_API_URL}/api/rooms/${formData.room_id}`)
                    .then(roomResponse => {
                        if (roomResponse.data) {
                            console.log(`🏨 Agregando manualmente la habitación en edición: ${roomResponse.data.room_number}`);
                            setRooms([...availableRooms, roomResponse.data]); // 🔹 Agregarla a la lista
                        }
                    })
                    .catch(error => console.error("❌ Error obteniendo la habitación actual:", error));
            } else {
                console.log(`✅ La habitación ${formData.room_id} ya está en la lista.`);
                // 🔹 Si la habitación actual no está en la lista, agregarla manualmente
                if (formData.room_id && !availableRooms.some(room => room.id === Number(formData.room_id))) {
                    axios.get(`${process.env.REACT_APP_API_URL}/api/rooms/${formData.room_id}`)
                        .then(roomResponse => {
                            if (roomResponse.data) {
                                console.log(`🏨 Agregando manualmente la habitación en edición: ${roomResponse.data.room_number}`);
                                setRooms([...availableRooms, roomResponse.data]); // 🔹 Agregarla a la lista
                            }
                        })
                        .catch(error => console.error("❌ Error obteniendo la habitación actual:", error));
                } else {
                    setRooms(availableRooms);
                }

            }
        })
        .catch(error => console.error("❌ Error fetching available rooms:", error));
    } else {
        setRooms([]);
    }
}, [formData.check_in_date, formData.check_out_date, formData.business_unit_id, businessUnitId, formData.room_category_id, formData.room_id, id]);


    
    
    // Calcular el número de noches automáticamente al cambiar Check-in o Check-out
useEffect(() => {
        if (!formData.check_in_date || !formData.check_out_date) return;
    
        const checkIn = new Date(formData.check_in_date);
        const checkOut = new Date(formData.check_out_date);
        const diffDays = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
    
        setFormData(prev => ({ ...prev, room_nights: diffDays > 0 ? diffDays : 1 })); 
}, [formData.check_in_date, formData.check_out_date]);
    
useEffect(() => {
        document.body.classList.add("check-in-scroll");
    
        return () => {
            document.body.classList.remove("check-in-scroll");
        };
}, []);
    
useEffect(() => {
        const handlePopState = async () => {
            console.log("🔄 Usuario presionó 'Atrás', guardando antes de salir...");
            
            try {
                const updatePayload = buildReservationUpdatePayload(
                    formData,
                    originalFormData
                );
                if (Object.keys(updatePayload).length > 0) {
                    await axios.put(
                        `${process.env.REACT_APP_API_URL}/api/reservations/${id}`,
                        updatePayload
                    );
                    console.log("✅ Reserva guardada correctamente antes de salir.");
                }
            } catch (error) {
                console.error("❌ Error guardando reserva antes de salir:", error);
            }
    
            // 🔹 Redirigir a la página de detalles con room_id
            navigate(`/reservation/${id}?business_unit_id=${formData.business_unit_id || businessUnitId}&room_id=${formData.room_id}`, { replace: true });
        };
    
        // Agregar evento cuando el usuario presiona "Atrás"
        window.addEventListener("popstate", handlePopState);
    
        return () => {
            // Remover evento cuando el componente se desmonta
            window.removeEventListener("popstate", handlePopState);
        };
}, [id, businessUnitId, formData, navigate, originalFormData]);



    // 🆕 Cargar valores por defecto si faltan
useEffect(() => {
  const fetchDefaultTimesIfNeeded = async () => {
    if (!formData.check_in_time || !formData.check_out_time) {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
          params: { business_unit_id: formData.business_unit_id || businessUnitId }
        });

        const settings = response.data;

        setFormData((prev) => ({
          ...prev,
          check_in_time: prev.check_in_time || settings.checkin_time || "",
          check_out_time: prev.check_out_time || settings.checkout_time || "",
        }));
        setOriginalFormData((prev) =>
          prev
            ? {
                ...prev,
                check_in_time: prev.check_in_time || settings.checkin_time || "",
                check_out_time: prev.check_out_time || settings.checkout_time || "",
              }
            : prev
        );
      } catch (error) {
        console.warn("⚠️ No se pudieron cargar tiempos por defecto:", error);
      }
    }
  };

  if (formData && formData.business_unit_id) {
    fetchDefaultTimesIfNeeded();
  }
}, [formData, businessUnitId]);

    if (!formData) return <div>Loading...</div>; // Evitar renderizado antes de cargar los datos

    // Manejar cambios en los inputs
    const handleChange = (e) => {
        const { name, value } = e.target;
      
        setFormData(prev => ({ ...prev, [name]: value }));
      };
      
    

    // Manejar la actualización de la reserva
    const handleSubmit = async (e) => {
        console.log("🔼 Enviando actualización con datos:", formData);
        e.preventDefault();

        try {
            const updatePayload = buildReservationUpdatePayload(
                formData,
                originalFormData
            );
            if (Object.keys(updatePayload).length > 0) {
                await axios.put(
                    `${process.env.REACT_APP_API_URL}/api/reservations/${id}`,
                    updatePayload
                );
            }
            const effectiveBusinessUnitId = formData.business_unit_id || businessUnitId;
            console.log("🔄 Navegando a:", `/reservation/${id}?business_unit_id=${effectiveBusinessUnitId}&room_id=${formData.room_id}`);
            navigate(`/reservation/${id}?business_unit_id=${effectiveBusinessUnitId}&room_id=${formData.room_id}`);
        } catch (error) {
            console.error("Error updating reservation:", error);
            alert("Failed to update reservation");
        }
    };


    const mainGuest = formData?.guests?.find(guest => guest.is_main_guest);
    let currentUser = null;
    try {
        currentUser = JSON.parse(localStorage.getItem("palantir_user") || "null");
    } catch (_error) {
        currentUser = null;
    }
    const reservationBusinessUnitId = Number(formData.business_unit_id) || null;
    const workflowBusinessUnitError = originalFormData
        ? getReservationWorkflowBusinessUnitError({
            activeBusinessUnitId: currentUser?.active_business_unit_id,
            reservationBusinessUnitId,
          })
        : "";
    const ratesPricesUrl = workflowBusinessUnitError ? null : buildRatesPricesUrl({
        reservationGroupId: formData.reservation_group_id,
        businessUnitId: reservationBusinessUnitId,
        reservationId: id,
    });
    const reservationStudioUrl = workflowBusinessUnitError ? null : buildReservationStudioUrl({
        reservationGroupId: formData.reservation_group_id,
        businessUnitId: reservationBusinessUnitId,
    });
    

    
    return (
        <div className="d-flex justify-content-center">
    <form 
        onSubmit={handleSubmit} 
        className="container fs-6"
        style={{ transform: "scale(0.85)", transformOrigin: "top center", width: "120%" }}
    >
        <div className="card bg-dark text-light p-4">
            <h2 className="fs-5 text-center mb-3 fw-bold">Edit Reservation</h2>

            {/* Información del Huésped Principal */}
            {mainGuest && mainGuest.customer && (
              <div className="mb-4 fw-bold">
                <h3 className="text-warning">Main Guest Information</h3>
                <div className="p-3 bg-secondary rounded d-flex justify-content-between align-items-start gap-4">
                  <div>
                    <p><strong>Name:</strong> {mainGuest.customer.first_name} {mainGuest.customer.last_name}</p>
                    <p><strong>Email:</strong> {mainGuest.customer.email || "N/A"}</p>
                    <p><strong>Phone:</strong> {mainGuest.customer.phone || "N/A"}</p>
                    <p><strong>Country:</strong> {mainGuest.customer.country || "N/A"}</p>
                  </div>

                  <div>
                    <img
                      src={mainGuest.customer.photo_url || "/default-avatar.png"}
                      alt="Customer"
                      style={{
                        width: "100px",
                        height: "100px",
                        objectFit: "cover",
                        borderRadius: "10px",
                        border: "2px solid #ddd"
                      }}
                      onError={(e) => {
                        if (e.target.src !== window.location.origin + "/default-avatar.png") {
                          e.target.src = "/default-avatar.png";
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            )}


            {/* Formulario de Edición de Reserva */}
            <div className="row">
                <div className="col-md-6 mb-3">
                    <label className="form-label">Check-in Date</label>
                    <input
                    type="date"
                    name="check_in_date"
                    value={formData.check_in_date}
                    readOnly
                    className="form-control bg-secondary text-light"
                    />

                </div>
                <div className="col-md-6 mb-3">
                    <label className="form-label">Check-in Time</label>
                    <input type="time" name="check_in_time" value={formData.check_in_time} onChange={handleChange} required className="form-control form-control-sm p-1" />
                </div>
                <div className="col-md-6 mb-3">
                    <label className="form-label">Check-out Date</label>
                    <input
                        type="date"
                        name="check_out_date"
                        value={formData.check_out_date}
                        readOnly
                        className="form-control bg-secondary text-light"
                        />

                </div>
                <div className="col-md-6 mb-3">
                    <label className="form-label">Check-out Time</label>
                    <input type="time" name="check_out_time" value={formData.check_out_time} onChange={handleChange} required className="form-control form-control-sm p-1" />
                </div>
                <div className="col-md-6 mb-3">
                    <label className="form-label">Number of Nights</label>
                    <input type="number" name="room_nights" value={formData.room_nights} readOnly className="form-control bg-secondary text-light" />
                </div>
                <div className="col-md-6 mb-3">
                    <label className="form-label">Number of Adults</label>
                    <input
                        type="number"
                        name="adults"
                        value={formData.adults}
                        readOnly
                        className="form-control bg-secondary text-light"
                    />

                </div>
                <div className="col-md-6 mb-3">
                    <label className="form-label">Number of Children</label>
                    <input
                        type="number"
                        name="children"
                        value={formData.children}
                        readOnly
                        className="form-control bg-secondary text-light"
                    />

                </div>
                <div className="col-md-6 mb-3">
                    <label className="form-label">Children Ages (comma-separated)</label>
                    <input type="text" name="children_ages" value={formData.children_ages} readOnly className="form-control bg-secondary text-light" />
                </div>
                
                <div className="col-md-6 mb-3">
                    <label className="form-label">Room Category</label>
                    <input
                        type="text"
                        value={roomCategoryLabel}
                        readOnly
                        className="form-control bg-secondary text-light"
                    />
                </div>

                <div className="col-md-6 mb-3">
                    <label className="form-label">Room</label>
                    <select name="room_id" value={formData.room_id || ""} onChange={handleChange} optional className="form-select form-control-sm p-1">
                        <option value="">Select a Room</option>
                        {rooms.map((room) => (
                            <option key={room.id} value={room.id}>
                                {room.room_number} - {room.description} ({room.view})
                            </option>
                        ))}
                    </select>
                </div>

                <div className="col-md-6 mb-3">
                    <label className="form-label">Rate</label>
                    <input
                        type="text"
                        value={rateLabel}
                        readOnly
                        className="form-control bg-secondary text-light"
                    />
                </div>
                <div className="col-md-12 mb-3">
                    <label className="form-label">Special Requests</label>
                    <textarea 
                        name="special_requests" 
                        value={formData.special_requests} 
                        onChange={handleChange} 
                        className="form-control">
                    </textarea>
                </div>

                
                
                <div className="col-md-6 mb-3">
                    <label className="form-label">Total Price (base {baseCurrency})</label>
                    <input type="number" name="total_price" value={formData.total_price || 0} readOnly className="form-control bg-secondary text-light" />
                </div>

                <div className="col-md-12 mb-3">
                    <div className="alert alert-info mb-0" role="status">
                        <div>
                            Stay dates, occupancy, category, rates, prices and overrides are managed in Reservation Studio so availability, nightly totals and accounting remain synchronized.
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline-info btn-sm mt-2 me-2 fw-bold"
                            disabled={!reservationStudioUrl}
                            onClick={() => reservationStudioUrl && navigate(reservationStudioUrl)}
                        >
                            Open Reservation Studio
                        </button>
                        <button
                            type="button"
                            className="btn btn-info btn-sm mt-2 fw-bold"
                            disabled={!ratesPricesUrl}
                            onClick={() => ratesPricesUrl && navigate(ratesPricesUrl)}
                        >
                            Open Rates &amp; Prices
                        </button>
                        {workflowBusinessUnitError ? (
                            <div className="small mt-2 text-warning">
                                {workflowBusinessUnitError}
                            </div>
                        ) : !ratesPricesUrl ? (
                            <div className="small mt-2">
                                This reservation is not linked to a reservation group, so the dedicated commercial workflows are unavailable here.
                            </div>
                        ) : null}
                    </div>
                </div>
                
                

                <div className="col-md-6 mb-3">
                    <label className="form-label">Reservation Status</label>
                    <input 
                        type="text" 
                        name="status" 
                        value={formData.status} 
                        readOnly 
                        className="form-control bg-secondary text-light"
                    />
                </div>

            </div>

            <button 
                type="submit" 
                disabled={!originalFormData}
                className="btn text-white fw-bold border-0 w-100 mt-3"
                style={{ 
                    background: "linear-gradient(to right, #9b59b6, #8e44ad, #5B2C6F)", 
                    borderRadius: "5px", 
                    padding: "10px 20px"
                }}
            >
                Update Reservation
            </button>
        </div>
    </form>
</div>


    );
};

export default EditReservation;
